import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LodashUtils, BranchUtils } from "utils";
import { AppConstant, ImageConstant, LangConstant } from "const";
import { useUpdateBranch, useUploadBranchAvatar } from "hooks";

const CompanyProfile = ({ branchDetails }) => {
  const { t: getLabel } = useTranslation([LangConstant.NS_COMMON, LangConstant.NS_LOGIN]);

  const CompanyTagNameIcon = ImageConstant.CompanyTagNameIcon;
  const errorObjectContent = getLabel(LangConstant.ERR_OBJECT, { ns: LangConstant.NS_LOGIN, returnObjects: true });
  const { uploadBranchAvatarAsync } = useUploadBranchAvatar();
  const { updateBranchInfo } = useUpdateBranch();

  const [form, setForm] = useState({ companyName: "", companyAvatar: null, companyAvatarId: "" });
  const [initForm, setInitForm] = useState({ companyName: "", companyAvatar: null, companyAvatarId: "" });
  const [validatedMsg, setValidatedMsg] = useState("");

  const handleChangeCompanyName = e => {
    const name = e.target.value;
    if (name.length < AppConstant.COMPANY_NAME_LENGTH_LIMITED) {
      setForm(currentForm => ({ ...currentForm, companyName: name }));
    }
  };

  const handleChangeCompanyAvatar = async e => {
    if (e.target.value.length !== 0) {
      const avatar = e.target.files[0];
      if (!validateAvatar(avatar)) {
        setValidatedMsg(errorObjectContent.imageNotValid);
        setForm(currentForm => ({
          ...currentForm,
          companyAvatar: initForm.companyAvatar,
          companyAvatarId: initForm.companyAvatarId,
        }));
        return;
      }
      const avatarId = await uploadBranchAvatarAsync(avatar);
      if (!avatarId) {
        setValidatedMsg(errorObjectContent.imageNotValid);
        setForm(currentForm => ({
          ...currentForm,
          companyAvatar: initForm.companyAvatar,
          companyAvatarId: initForm.companyAvatarId,
        }));
        return;
      }
      const previewAvatar = URL.createObjectURL(avatar);
      setValidatedMsg("");
      setForm(currentForm => ({ ...currentForm, companyAvatar: previewAvatar, companyAvatarId: avatarId }));
    }
  };

  const handleDiscardFormData = () => {
    const fileInput = document.getElementById("companyAvatar");
    fileInput.value = null;
    setForm({ ...initForm });
    setValidatedMsg("");
  };

  const handleFormSubmit = async event => {
    event.preventDefault();
    const currentBranchDetails = BranchUtils.getCurrentBranch();
    const payload = { ...currentBranchDetails, name: form.companyName, attachmentId: form.companyAvatarId };
    updateBranchInfo(payload);
  };

  useEffect(() => {
    if (branchDetails) {
      const data = {
        companyName: branchDetails.name,
        companyAvatar: branchDetails.avatar,
        companyAvatarId: branchDetails.attachmentId,
      };
      setForm(state => ({ ...state, ...data }));
      setInitForm(state => ({ ...state, ...data }));
    }
  }, [branchDetails]);

  return (
    <form onSubmit={handleFormSubmit}>
      <div className="d-flex align-items-start gap-3" style={{ marginBottom: 16 }}>
        <div style={{ display: "block" }}>
          <span
            className="semiBold-sm-txt"
            style={{ lineHeight: "20px", color: "#6C7078", marginBottom: 8, display: "block" }}
          >
            {getLabel(LangConstant.L_COMPANY_AVATAR)}
          </span>
          <input
            id="companyAvatar"
            type="file"
            className="d-none"
            onChange={handleChangeCompanyAvatar}
            accept={AppConstant.ACCEPTABLE_IMAGE_TYPES.join(",")}
          />
          <div className="d-flex align-items-center">
            {Boolean(form.companyAvatar || initForm.companyAvatar) && (
              <div
                style={{
                  width: 41,
                  height: 41,
                  borderRadius: "50%",
                  marginRight: 16,
                  border: "1px solid #e1e1e1",
                  backgroundImage: `url(${form.companyAvatar || initForm.companyAvatar})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
            )}
            <label
              htmlFor="companyAvatar"
              className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
              style={{ minWidth: 160, height: 41, cursor: "pointer" }}
            >
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                {getLabel(LangConstant.L_CHANGE_COMPANY_AVATAR_BUTTON)}
              </span>
            </label>
          </div>
          {validatedMsg && (
            <span className="regular-sm-txt" style={{ lineHeight: "16px", color: "#FF2C28", display: "block", margin: "4px 66px 0" }}>
              {validatedMsg}
            </span>
          )}
        </div>
        <div style={{ alignItems: "flex-start", margin: 0 }}>
          <span
            className="semiBold-sm-txt"
            style={{ lineHeight: "20px", color: "#6C7078", marginBottom: 8, display: "block" }}
          >
            {getLabel(LangConstant.L_COMPANY_NAME)}
          </span>
          <div className="input-group" style={{ minWidth: 400 }}>
            <span className="input-group-text">
              <CompanyTagNameIcon style={{ color: "#000" }} />
            </span>
            <input
              type="text"
              className="form-control"
              id="companyName"
              onChange={handleChangeCompanyName}
              value={form.companyName}
              placeholder={getLabel(LangConstant.P_COMPANY_NAME)}
              style={{ height: 41 }}
            />
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-end">
        <button
          disabled={LodashUtils.isEqual(form, initForm) || validatedMsg !== "" || form.companyName === ""}
          className="btn btn-primary semiBold-sm-txt"
          type="submit"
          style={{ lineHeight: "20px", minWidth: 164, marginRight: 19, padding: "8px 16px" }}
        >
          {getLabel(LangConstant.L_CONFIRM_BUTTON)}
        </button>
        <button
          className="btn btn-secondary semiBold-sm-txt"
          disabled={LodashUtils.isEqual(form, initForm)}
          onClick={handleDiscardFormData}
          type="reset"
          style={{ lineHeight: "20px", minWidth: 164, padding: "8px 16px" }}
        >
          {getLabel(LangConstant.L_DISCARD_BUTTON)}
        </button>
      </div>
    </form>
  );
};

export default CompanyProfile;

const validateAvatar = avatar => {
  return avatar && AppConstant.ACCEPTABLE_IMAGE_TYPES.includes(avatar.type);
};
